import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import {
  HomeIcon,
  ChartBarIcon,
  LightBulbIcon,
  PlayIcon,
  SearchIcon,
} from '@/components/icons/NavIcons';

interface NavTab {
  id: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const mainTabs: NavTab[] = [
  { id: 'overview', href: '/', icon: HomeIcon },
  { id: 'understand', href: '/understand', icon: ChartBarIcon },
  { id: 'implications', href: '/implications', icon: LightBulbIcon },
  { id: 'simulations', href: '/decide', icon: PlayIcon },
  { id: 'research', href: '/research', icon: SearchIcon },
];

export function Navigation() {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { selectedClient } = useClient();

  // Context-based client for dropdown display
  const selectedClientId = selectedClient?.client_id || null;

  // URL-based client for sub-nav (only show on /clients/:id routes)
  const clientRouteMatch = location.pathname.match(/\/clients\/([^/]+)/);
  const urlClientId = clientRouteMatch ? clientRouteMatch[1] : null;

  const isMainTabActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/' || location.pathname === '/overview';
    }
    return location.pathname.startsWith(href);
  };

  const getTabName = (id: string): string => {
    const names: Record<string, string> = {
      overview: t.overview,
      understand: t.understand,
      implications: t.implications,
      simulations: t.simulations || 'Simulations',
      research: t.research || (language === 'es' ? 'Investigación' : 'Research'),
    };
    return names[id] || id;
  };

  // Format current date
  const formatDate = () => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return now.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  return (
    <nav className="bg-surface-800 border-b border-surface-400/20">
      <div className="flex items-center h-14 max-w-[1600px]">
        {/* Logo - Aligned with sidebar width (w-64 = 256px, w-16 = 64px collapsed) */}
        <div className="w-64 flex-shrink-0 px-3 border-r border-surface-400/20">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">NM</span>
            </div>
            <span className="text-base font-semibold text-white group-hover:text-primary-200 transition-colors">
              Simulator
            </span>
          </Link>
        </div>

        {/* Main content area */}
        <div className="flex-1 flex items-center px-6">
          {/* Main Navigation Tabs - VoterPulse style with underline */}
          <div className="flex items-center">
            <div className="flex">
              {mainTabs.map((tab) => {
                const isActive = isMainTabActive(tab.href);
                const IconComponent = tab.icon;
                return (
                  <Link
                    key={tab.id}
                    to={tab.href}
                    className={`
                      nav-tab px-4 py-3 text-sm font-medium transition-all
                      flex items-center gap-2 relative
                      ${isActive
                        ? 'text-white'
                        : 'text-text-secondary hover:text-white'
                      }
                    `}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{getTabName(tab.id)}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-400 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side - pushed to far right */}
          <div className="flex items-center gap-4 pr-2">
            {/* Date display */}
            <span className="text-xs text-text-secondary hidden md:block">
              {formatDate()}
            </span>

            {/* Language toggle - VoterPulse style EN|ES */}
            <div className="flex items-center text-xs font-medium">
              <button
                onClick={() => setLanguage('en')}
                className={`px-1.5 py-0.5 transition-colors ${
                  language === 'en' ? 'text-white' : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                EN
              </button>
              <span className="text-surface-400">|</span>
              <button
                onClick={() => setLanguage('es')}
                className={`px-1.5 py-0.5 transition-colors ${
                  language === 'es' ? 'text-white' : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                ES
              </button>
            </div>

            {/* Client selector */}
            <ClientSelector currentClientId={selectedClientId} />

            {/* Help button */}
            <HelpButton language={language} />

            {/* User avatar placeholder */}
            <button className="w-8 h-8 rounded-full bg-surface-600 hover:bg-surface-500 flex items-center justify-center text-text-secondary hover:text-white transition-colors">
              <UserIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Client sub-navigation - Only visible when on /clients/:id routes */}
      {urlClientId && <ClientSubNav clientId={urlClientId} />}
    </nav>
  );
}

function ClientSubNav({ clientId }: { clientId: string }) {
  const location = useLocation();
  const { t } = useLanguage();
  const basePath = `/clients/${clientId}`;

  const clientTabs = [
    { id: 'dashboard', href: '', label: 'Dashboard' },
    { id: 'market', href: '/market', label: t.market },
    { id: 'pricing', href: '/pricing', label: t.pricing },
    { id: 'launch', href: '/launch', label: t.launch },
    { id: 'portfolio', href: '/portfolio', label: t.portfolio },
  ];

  const isActive = (href: string) => {
    const fullPath = basePath + href;
    if (href === '') {
      return location.pathname === basePath || location.pathname === basePath + '/';
    }
    return location.pathname.startsWith(fullPath);
  };

  return (
    <div className="bg-surface-700/30 border-t border-surface-400/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-9">
          {/* Client sub-tabs - underline style */}
          <div className="flex">
            {clientTabs.map((tab) => (
              <Link
                key={tab.id}
                to={basePath + tab.href}
                className={`
                  sub-tab px-3 py-2 text-xs font-medium transition-all relative
                  ${isActive(tab.href)
                    ? 'text-white'
                    : 'text-text-secondary hover:text-white'
                  }
                `}
              >
                {tab.label}
                {isActive(tab.href) && (
                  <span className="absolute bottom-0 left-1 right-1 h-0.5 bg-primary-400 rounded-full" />
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientSelector({ currentClientId }: { currentClientId: string | null }) {
  const { t } = useLanguage();
  const { clients, selectedClient, selectClient, isLoading } = useClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectClient = (clientId: string) => {
    setIsOpen(false);
    selectClient(clientId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm transition-colors ${
          currentClientId
            ? 'bg-primary-500/20 border border-primary-500/30 text-primary-200'
            : 'bg-surface-700/50 hover:bg-surface-600 border border-surface-400/20 text-text-secondary'
        }`}
      >
        <UsersIcon className={`w-4 h-4 ${currentClientId ? 'text-primary-300' : 'text-text-secondary'}`} />
        <span className="font-medium">
          {isLoading ? '...' : (selectedClient?.client_name || 'Clients')}
        </span>
        <ChevronDownIcon className={`transition-transform ${currentClientId ? 'text-primary-300' : 'text-text-secondary'} ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-64 bg-surface-700 border border-surface-400/30 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-surface-400/20">
            <span className="text-xs text-text-secondary font-medium uppercase tracking-wide">{t.selectClient}</span>
          </div>
          {isLoading ? (
            <div className="px-4 py-3 text-text-secondary text-sm">{t.loading}</div>
          ) : clients && clients.length > 0 ? (
            <div className="py-1 max-h-64 overflow-y-auto">
              {clients.map((client) => (
                <button
                  key={client.client_id}
                  onClick={() => handleSelectClient(client.client_id)}
                  className={`w-full px-3 py-2 text-left hover:bg-surface-600 transition-colors flex items-center gap-2.5 ${
                    client.client_id === currentClientId ? 'bg-surface-600' : ''
                  }`}
                >
                  <div className="w-7 h-7 rounded bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-200 text-xs font-bold">
                      {client.client_name.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium text-sm truncate">{client.client_name}</div>
                    <div className="text-xs text-text-secondary">
                      {client.market.country}
                    </div>
                  </div>
                  {client.client_id === currentClientId && (
                    <CheckIcon className="w-4 h-4 text-primary-400" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3 text-text-secondary text-sm">No clients available</div>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ChevronDownIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={`w-3.5 h-3.5 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function UsersIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function CheckIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function UserIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function HelpIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  );
}

function HelpButton({ language }: { language: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = language === 'es';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const helpItems = [
    {
      icon: '📖',
      title: t ? 'Guía de Inicio' : 'Getting Started',
      description: t ? 'Aprende los conceptos básicos' : 'Learn the basics',
    },
    {
      icon: '🎯',
      title: t ? 'Tutorial Interactivo' : 'Interactive Tutorial',
      description: t ? 'Recorrido paso a paso' : 'Step-by-step walkthrough',
      badge: t ? 'Próximamente' : 'Coming Soon',
    },
    {
      icon: '📊',
      title: t ? 'Glosario de Métricas' : 'Metrics Glossary',
      description: t ? 'Definiciones y cálculos' : 'Definitions and calculations',
    },
    {
      icon: '💬',
      title: t ? 'Soporte' : 'Support',
      description: t ? 'Contactar al equipo' : 'Contact the team',
    },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-full bg-surface-600 hover:bg-surface-500 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
        title={t ? 'Ayuda' : 'Help'}
      >
        <HelpIcon className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-72 bg-surface-700 border border-surface-400/30 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-400/20 bg-surface-800/50">
            <h3 className="text-sm font-semibold text-white">
              {t ? 'Centro de Ayuda' : 'Help Center'}
            </h3>
            <p className="text-xs text-text-secondary mt-0.5">
              {t ? 'Recursos y documentación' : 'Resources and documentation'}
            </p>
          </div>
          <div className="py-1">
            {helpItems.map((item, index) => (
              <button
                key={index}
                className="w-full px-4 py-2.5 text-left hover:bg-surface-600 transition-colors flex items-start gap-3"
                onClick={() => {
                  // TODO: Implement navigation to help sections
                  if (!item.badge) {
                    console.log('Navigate to:', item.title);
                  }
                }}
              >
                <span className="text-lg">{item.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{item.title}</span>
                    {item.badge && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-surface-500 text-text-secondary rounded">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5">{item.description}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-surface-400/20 bg-surface-800/30">
            <p className="text-[10px] text-text-tertiary text-center">
              {t ? 'Presiona ? en cualquier momento para ayuda' : 'Press ? anytime for help'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
